import React from 'react';
import { Typography, styled, useTheme, useMediaQuery, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toEuroWholeNumber } from '../utils/numberFormat';
import { CashbackAdvantage } from './DetailCalculator';

const FlexRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingBottom: theme.spacing(1),
}));

const BookNowButton = styled(Button)(({ theme }) => ({
  background: (theme.palette.common as any).sun,
  color: theme.palette.common.white,
  marginTop: theme.spacing(1),
  '&:hover': {
    background: theme.palette.primary.main,
  },
}));

const Underline = styled('div')({
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
});

const BackButton = styled(Button)(({ theme }) => ({
  color: theme.palette.action.active,
  cursor: 'pointer',
}));

interface Props {
  bookNowUrl: string;
  cashbackAdvantage: CashbackAdvantage;
  handleBackButtonClick: () => void;
}

export const CashbackAdvantageBox = ({
  cashbackAdvantage,
  handleBackButtonClick,
  bookNowUrl,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  return (
    <div>
      <FlexRow>
        <Typography variant="body1" component="div" color="textSecondary">
          <i>
            {t('cashbackCalculator.detailed.advantageBox.yourCashback')}
            &nbsp;&nbsp;
          </i>
        </Typography>
        <Typography variant="h4" component="div">
          {toEuroWholeNumber(cashbackAdvantage.cashback)}
        </Typography>
      </FlexRow>

      <FlexRow>
        <Typography variant="body1" component="div" color="textSecondary">
          <i>
            {t('cashbackCalculator.detailed.advantageBox.yourAgioDiscount')}
            &nbsp;&nbsp;
          </i>
        </Typography>
        <Typography variant="h4" component="div">
          {toEuroWholeNumber(cashbackAdvantage.totalAgio)}
        </Typography>
      </FlexRow>

      <FlexRow>
        <Typography variant="body1" component="div" color="textSecondary">
          <i>
            {t('cashbackCalculator.detailed.advantageBox.rentabloAdvantage')}
            &nbsp;&nbsp;
          </i>
        </Typography>
        <Typography variant="h2" component="div">
          <Underline>
            {toEuroWholeNumber(cashbackAdvantage.cashback + cashbackAdvantage.totalAgio)}
          </Underline>
        </Typography>
      </FlexRow>

      <FlexRow>
        <Typography variant="body1" component="div">
          <Underline>
            <i>{t('cashbackCalculator.detailed.advantageBox.yourCost')}</i>
          </Underline>
        </Typography>
      </FlexRow>

      <FlexRow>
        <BookNowButton variant="contained" size="large" fullWidth={isMobile} href={bookNowUrl}>
          {t('cashbackCalculator.detailed.advantageBox.applyButton')}
        </BookNowButton>
      </FlexRow>

      <FlexRow>
        <BackButton
          onClick={() => {
            handleBackButtonClick();
          }}
        >
          {`${t('cashbackCalculator.navigation.previous')}`}
        </BackButton>
      </FlexRow>
    </div>
  );
};
